import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import useRecommendations from '../hooks/useRecommendations';
import { useTrainingTimer } from '../hooks/useTrainingTimer';
import { useWakeLock } from '../hooks/useWakeLock';
import DrillSelector from '../components/training/DrillSelector';
import DrillVideoPlayer from '../components/training/DrillVideoPlayer';
import CompletedDrillItem from '../components/training/CompletedDrillItem';
import FocusSelector from '../components/training/FocusSelector';
import RecommendationCards from '../components/RecommendationCards';
import SessionRecoveryModal from '../components/training/SessionRecoveryModal';
import { trainingsDB, newTrainingId } from '../lib/db/trainings';
import { schoolDate, computeStreakUpdate } from '../lib/streak';
import { usersDB } from '../lib/db/users';
import { schoolsDB } from '../lib/db/schools';
import { calculateMultiplier, calculateDrillShurikens, calculateSessionBonusShurikens, DEFAULT_REWARD_CONFIG } from '../lib/shurikens';
import { customListsDB } from '../lib/db/customLists';
import { trackEvent, EVENTS } from '../lib/analytics';
import { callOvertake } from '../lib/api';
import { sessionStorage } from '../lib/sessionStorage';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import Modal from '../components/common/Modal';
import { useTimedDrill } from '../hooks/useTimedDrill';

export default function TrainingSession() {
  const [userData, setUserData] = useState(null);
  const [schoolData, setSchoolData] = useState(null);
  const [sessionTimer, setSessionTimer] = useState({ seconds: 0 });
  const [sessionTimerRunning, setSessionTimerRunning] = useState(false);
  const [currentDrill, setCurrentDrill] = useState(null);
  const [completedDrills, setCompletedDrills] = useState([]);
  const [showDrillSelector, setShowDrillSelector] = useState(false);
  const [drillNotes, setDrillNotes] = useState('');
  const [selectedFocusIds, setSelectedFocusIds] = useState([]);
  const [sessionNotes, setSessionNotes] = useState('');
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  // Mirrors currentSessionId synchronously, so a save started mid-render sees an ID claimed moments ago
  const sessionIdRef = useRef(null);
  // { id, created: Promise<boolean> } for the in-flight/last session doc creation
  const sessionCreateRef = useRef(null);
  const autoSaveInProgressRef = useRef(false);
  const [modalState, setModalState] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  const [editDrillState, setEditDrillState] = useState({ isOpen: false, drillIndex: null, minutes: 0, seconds: 0, selectedFocusIds: [] });
  const [lastDrillShurikens, setLastDrillShurikens] = useState(null); // { count, isGold } shown briefly after drill finish
  const recommendationCacheRef = useRef(new Map());
  const pendingFocusIdRef = useRef(null);
  const drillTimer = useTrainingTimer();
  const { currentUser } = useAuth();
  const { recommendations, dismissRecommendation } = useRecommendations();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const hasCheckedRecovery = useRef(false);
  const audioContextRef = useRef(null);
  
  // Video recording state
  const [videoFile, setVideoFile] = useState(null);
  const [videoDuration, setVideoDuration] = useState(0);
  const [videoPreviewUrl, setVideoPreviewUrl] = useState(null);
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const videoInputRef = useRef(null);
  const videoElementRef = useRef(null);
  


  // Custom Lists state
  const [customLists, setCustomLists] = useState([]);
  const [activeCustomList, setActiveCustomList] = useState(null);
  const [selectedDrills, setSelectedDrills] = useState([]);
  const [editingListId, setEditingListId] = useState(null);
  const [multiSelectMode, setMultiSelectMode] = useState(false);

  const onDrillStart = useCallback(async () => {
    if (!currentDrill) {
      return;
    }

    drillTimer.start();

    if (!sessionTimerRunning) {
      setSessionTimerRunning(true);
      if (completedDrills.length === 0 && !sessionStartTime) {
        setSessionStartTime(new Date().toISOString());
      }
    }

    trackEvent(EVENTS.DRILL_TIMER_STARTED, { drillId: currentDrill.id });
  }, [currentDrill, drillTimer, sessionTimerRunning, completedDrills, sessionStartTime]);

  const {
    timingEnabled,
    setTimingEnabled,
    timingValues,
    handleTimingValueChange,
    startCountdownAndDrill,
    pauseTiming,
    stopTiming,
    isCountdownActive,
    countdownDisplay
  } = useTimedDrill({
    currentDrill,
    isRunning: drillTimer.isRunning,
    elapsedSeconds: drillTimer.elapsedSeconds,
    onDrillStart,
    audioContext: audioContextRef
  });
  
  useEffect(() => () => {
    try {
      if (
        audioContextRef.current &&
        typeof audioContextRef.current.close === 'function' &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
      }
    } catch (err) {
      console.warn('Error closing audio context', err);
    }
  }, []);
  
  // Wake lock is active when a drill is running (timer is active)
  const isTrainingActive = currentDrill && drillTimer.isRunning;
  const { isWakeLockActive, isSupported: isWakeLockSupported } = useWakeLock(isTrainingActive);
  
  const showModal = (title, message, onConfirm = null, type = 'alert', variant = 'primary') => {
    setModalState({ isOpen: true, title, message, onConfirm, type, variant });
  };
  
  const hideModal = () => {
    setModalState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'alert', variant: 'primary' });
  };
  
  const handleModalConfirm = () => {
    if (modalState.onConfirm) {
      modalState.onConfirm();
    }
    hideModal();
  };
  
  // Check for recoverable session on mount
  useEffect(() => {
    if (hasCheckedRecovery.current) return;
    hasCheckedRecovery.current = true;
    
    if (sessionStorage.hasRecoverableSession()) {
      const data = sessionStorage.load();
      setRecoveryData(data);
      setShowRecoveryModal(true);
    }
  }, []);
  
  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const data = await usersDB.getUserData(currentUser.uid);
        setUserData(data);
        
        trackEvent(EVENTS.TRAINING_SESSION_STARTED);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError(err);
        trackEvent(EVENTS.ERROR_OCCURRED, {
          errorType: 'fetch_user_data_training',
          errorMessage: err.message
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchUserData();
  }, [currentUser]);

  // Fetch school data when userData is available
  useEffect(() => {
    const fetchSchoolData = async () => {
      if (!userData?.profile?.schoolId) return;
      
      try {
        const school = await schoolsDB.getSchool(userData.profile.schoolId);
        setSchoolData(school);
      } catch (err) {
        console.error('Error fetching school data:', err);
      }
    };
    
    fetchSchoolData();
  }, [userData]);
  
  // Fetch custom lists when user is available
  useEffect(() => {
    const fetchCustomLists = async () => {
      if (!currentUser?.uid) return;
      
      try {
        const lists = await customListsDB.getUserLists(currentUser.uid);
        setCustomLists(lists);
      } catch (err) {
        console.error('Error fetching custom lists:', err);
      }
    };
    
    fetchCustomLists();
  }, [currentUser]);
  
  useEffect(() => {
    let interval;
    if (sessionTimerRunning) {
      interval = setInterval(() => {
        setSessionTimer(prev => ({ seconds: prev.seconds + 1 }));
      }, 1000);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [sessionTimerRunning]);
  
  // Close edit popup on Escape key and prevent body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && editDrillState.isOpen) {
        setEditDrillState({ isOpen: false, drillIndex: null, minutes: 0, seconds: 0, selectedFocusIds: [] });
      }
    };
    
    if (editDrillState.isOpen) {
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleEscape);
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleEscape);
    };
  }, [editDrillState.isOpen]);
  
  // Save session data whenever it changes
  useEffect(() => {
    if (isRecovering) return; // Don't save while recovering
    
    // Don't save if we're on the initial load and haven't started yet
    if (!sessionStartTime && !currentDrill && completedDrills.length === 0) {
      return;
    }
    
    const sessionData = {
      sessionStartTime,
      currentDrill,
      drillElapsedSeconds: drillTimer.elapsedSeconds,
      drillNotes,
      selectedFocusIds,
      sessionNotes,
      completedDrills,
      sessionElapsedSeconds: sessionTimer.seconds,
      userId: currentUser?.uid,
      sessionId: currentSessionId
    };
    
    sessionStorage.save(sessionData);
     
  }, [sessionStartTime, currentDrill, drillTimer.elapsedSeconds, drillNotes, selectedFocusIds, sessionNotes,
      completedDrills, sessionTimer.seconds, currentUser, isRecovering, currentSessionId]);
  
  const handleResumeSession = () => {
    setIsRecovering(true);
    setShowRecoveryModal(false);
    
    // Restore session state
    if (recoveryData) {
      setSessionStartTime(recoveryData.sessionStartTime);
      setCompletedDrills(recoveryData.completedDrills || []);
      setSessionTimer({ seconds: recoveryData.sessionElapsedSeconds || 0 });
      setSessionId(recoveryData.sessionId || null);
      
      // Restore current drill if exists
      if (recoveryData.currentDrill) {
        setCurrentDrill(recoveryData.currentDrill);
        setDrillNotes(recoveryData.drillNotes || '');
        setSelectedFocusIds(recoveryData.selectedFocusIds || []);
        
        // Restore drill timer elapsed time
        drillTimer.setElapsed(recoveryData.drillElapsedSeconds || 0);
      }
      
      // Restore session notes
      setSessionNotes(recoveryData.sessionNotes || '');
    }
    
    setIsRecovering(false);
  };
  
  const setSessionId = (id) => {
    sessionIdRef.current = id;
    setCurrentSessionId(id);
  };

  // Create the session's training doc on the first save, update it on later saves.
  // The ID is claimed before any await, so a Finish tapped while the first save is still
  // in flight (slow network, or offline where writes never resolve) updates the same doc
  // instead of creating a duplicate.
  const saveSession = async (trainingPayload) => {
    const sessionId = sessionIdRef.current;
    if (sessionId) {
      const pendingCreate = sessionCreateRef.current?.id === sessionId ? sessionCreateRef.current.created : null;
      // Wait for the create to be committed (locally) so the update has a doc to apply to
      if (!pendingCreate || await pendingCreate) {
        await trainingsDB.updateTrainingAndDate(currentUser.uid, sessionId, trainingPayload);
        return null;
      }
      // The create failed and nothing was written - fall through and create it now
    }

    const trainingId = newTrainingId();
    setSessionId(trainingId);
    const now = new Date().toISOString();
    const createPromise = trainingsDB.logTraining(currentUser.uid, {
      ...trainingPayload,
      startedAt: trainingPayload.startedAt || now,
      completedAt: now
    }, trainingId);
    sessionCreateRef.current = { id: trainingId, created: createPromise.then(() => true, () => false) };
    try {
      return await createPromise;
    } catch (err) {
      if (sessionIdRef.current === trainingId) setSessionId(null);
      throw err;
    }
  };

  const handleDiscardSession = () => {
    sessionStorage.clear();
    setShowRecoveryModal(false);
    setRecoveryData(null);
    setSessionId(null);
  };
  
  const handleSwipeLeft = (drillId) => {
    console.log('Swiped:', drillId);
    dismissRecommendation(drillId);
  };

  const handleRecommendationClick = async (recommendation) => {
    if (!userData?.profile?.schoolId || !recommendation?.id) {
      return;
    }

    const cacheKey = recommendation.id;
    let drill = recommendationCacheRef.current.get(cacheKey);

    try {
      if (!drill) {
        drill = await schoolsDB.getDrill(userData.profile.schoolId, recommendation.id);
        if (drill) {
          recommendationCacheRef.current.set(cacheKey, drill);
        }
      }

      if (!drill) {
        showModal(
          t('training.drill_not_found', 'Drill not found'),
          t('training.drill_not_found_message', 'We could not load that drill. Please try another recommendation.')
        );
        return;
      }

      // Store the focus ID to be set after drill is loaded
      if (recommendation.focusId) {
        pendingFocusIdRef.current = recommendation.focusId;
      }

      // Select the drill - the useEffect will handle setting the focus
      setCurrentDrill(drill);
      setShowDrillSelector(false);
      setDrillNotes('');
      setMultiSelectMode(false);
      drillTimer.reset();
      trackEvent(EVENTS.DRILL_SELECTED, { 
        drillId: drill.id, 
        drillName: drill[`name${i18n.language === 'he' ? 'He' : 'En'}`]
      });

      // Dismiss the recommendation after selecting
      dismissRecommendation(recommendation.id);
    } catch (err) {
      console.error('Error loading recommended drill:', err);
      showModal(
        t('error.server_error'),
        err.message || t('training.drill_not_found_message', 'We could not load that drill. Please try another recommendation.')
      );
    }
  };
  
  const handleDrillSelected = (drill) => {
    setCurrentDrill(drill);
    setShowDrillSelector(false);
    setDrillNotes('');
    setSelectedFocusIds([]);
    setMultiSelectMode(false);
    drillTimer.reset();
    trackEvent(EVENTS.DRILL_SELECTED, {
      drillId: drill.id,
      drillName: drill[`name${i18n.language === 'he' ? 'He' : 'En'}`]
    });
  };
  
  // Custom Lists handlers
  const handleLoadCustomList = async (listId) => {
    try {
      const list = await customListsDB.getList(listId);
      if (!list) return;
      
      // Update last used
      await customListsDB.updateLastUsed(listId);
      
      // Set active list
      setActiveCustomList({
        listId: list.listId,
        name: list.name,
        drillIds: list.drillIds,
        currentIndex: 0
      });
      
      // Load first drill
      if (list.drillIds.length > 0) {
        const firstDrill = await schoolsDB.getDrill(userData.profile.schoolId, list.drillIds[0]);
        if (firstDrill) {
          setCurrentDrill(firstDrill);
          setDrillNotes('');
          setSelectedFocusIds([]);
          drillTimer.reset();
        }
      }
      
      // Refresh lists to update lastUsed
      const lists = await customListsDB.getUserLists(currentUser.uid);
      setCustomLists(lists);
      
      trackEvent(EVENTS.CUSTOM_LIST_STARTED_DROPDOWN, {
        listId: list.listId,
        listName: list.name
      });
    } catch (err) {
      console.error('Error loading custom list:', err);
      showModal(t('error.server_error'), err.message);
    }
  };
  
  const handleToggleDrill = (drillId) => {
    setSelectedDrills(prev => {
      if (prev.includes(drillId)) {
        return prev.filter(id => id !== drillId);
      } else {
        return [...prev, drillId];
      }
    });
  };
  
  const handleLoadListInSelector = async (listId) => {
    try {
      const list = await customListsDB.getList(listId);
      if (!list) return;
      
      setSelectedDrills(list.drillIds);
      setEditingListId(listId);
    } catch (err) {
      console.error('Error loading list in selector:', err);
    }
  };
  
  const handleClearSelections = () => {
    setSelectedDrills([]);
    setEditingListId(null);
  };
  
  const handleReorderDrills = (newOrderedDrillIds) => {
    setSelectedDrills(newOrderedDrillIds);
  };
  
  const handleSaveList = async (listId, drillIds, listName = null) => {
    try {
      let finalListId = listId;
      
      if (!listId) {
        // Create new list
        if (!listName) {
          listName = prompt(t('drill_selector.list_name_prompt'));
          if (!listName || !listName.trim()) return;
        }
        
        const result = await customListsDB.createList(currentUser.uid, {
          name: listName.trim(),
          drillIds
        });
        finalListId = result.listId;
        
        trackEvent(EVENTS.CUSTOM_LIST_CREATED, {
          listName: listName.trim(),
          drillCount: drillIds.length
        });
      } else {
        // Update existing list
        await customListsDB.updateList(listId, { drillIds });
        
        trackEvent(EVENTS.CUSTOM_LIST_EDITED, {
          listId,
          drillCount: drillIds.length
        });
      }
      
      // Refresh lists
      const lists = await customListsDB.getUserLists(currentUser.uid);
      setCustomLists(lists);
      
      // Start first drill from list
      if (drillIds.length > 0) {
        const firstDrill = await schoolsDB.getDrill(userData.profile.schoolId, drillIds[0]);
        if (firstDrill) {
          setActiveCustomList({
            listId: finalListId,
            name: listName || customLists.find(l => l.listId === listId)?.name || '',
            drillIds,
            currentIndex: 0
          });
          setCurrentDrill(firstDrill);
          setShowDrillSelector(false);
          setDrillNotes('');
          setSelectedFocusIds([]);
          setMultiSelectMode(false);
          setSelectedDrills([]);
          setEditingListId(null);
          drillTimer.reset();
          
          trackEvent(EVENTS.CUSTOM_LIST_STARTED_SAVE, {
            listId: finalListId,
            listName: listName || customLists.find(l => l.listId === listId)?.name || ''
          });
        }
      }
    } catch (err) {
      console.error('Error saving list:', err);
      showModal(t('error.server_error'), err.message);
    }
  };
  
  const handleDeleteList = async (listId) => {
    try {
      await customListsDB.deleteList(listId);
      
      // Refresh lists
      const lists = await customListsDB.getUserLists(currentUser.uid);
      setCustomLists(lists);
      
      // If the deleted list was being edited, clear the editing state
      if (editingListId === listId) {
        setSelectedDrills([]);
        setEditingListId(null);
      }
      
      // If the deleted list was active, clear it
      if (activeCustomList?.listId === listId) {
        setActiveCustomList(null);
        if (!currentDrill || (currentDrill.id === 'custom')) {
          setCurrentDrill(null);
        }
      }
    } catch (err) {
      console.error('Error deleting list:', err);
      showModal(t('error.server_error'), err.message);
    }
  };
  
  const handleOpenDrillSelector = async () => {
    try {
      const data = await usersDB.getUserData(currentUser.uid);
      setUserData(data);
    } catch (err) {
      console.error('Error refreshing user data for drill selector:', err);
    }
    setShowDrillSelector(true);
    // Don't force multiSelectMode - let DrillSelector manage its own selectionMode
    // DrillSelector defaults to 'single' mode
    setSelectedDrills([]);
    setEditingListId(null);
  };
  
  const handleCloseDrillSelector = () => {
    setShowDrillSelector(false);
    setMultiSelectMode(false);
    setSelectedDrills([]);
    setEditingListId(null);
  };

  // Effect to set focus when drill changes and we have a pending focus ID
  useEffect(() => {
    if (currentDrill && pendingFocusIdRef.current && schoolData?.focus) {
      const applicableFocus = getApplicableFocusForCategory(currentDrill.category);
      const focusIsApplicable = applicableFocus.some(f => f.id === pendingFocusIdRef.current);
      
      if (focusIsApplicable) {
        setSelectedFocusIds([pendingFocusIdRef.current]);
      }
      pendingFocusIdRef.current = null;
    }
  }, [currentDrill, schoolData]);

  // Get applicable focus items for the current drill
  const getApplicableFocus = () => {
    if (!currentDrill) return [];
    if (!schoolData?.focus || !currentDrill?.category) return [];
    
    return schoolData.focus.filter(focusItem => 
      focusItem.categories && focusItem.categories.includes(currentDrill.category)
    );
  };

  // Get applicable focus items for a drill by category
  const getApplicableFocusForCategory = (category) => {
    if (!schoolData?.focus || !category) return [];
    
    return schoolData.focus.filter(focusItem => 
      focusItem.categories && focusItem.categories.includes(category)
    );
  };
  
  const handleStartTimer = async () => {
    if (isCountdownActive) {
      return;
    }
    
    if (currentDrill?.id === 'custom' && !drillNotes.trim()) {
      showModal(
        t('training.notes_required'),
        t('training.notes_required_before_start_message') || '',
        null,
        'alert',
        'warning'
      );
      return;
    }
    
    await startCountdownAndDrill();
  };
  
  const handlePauseTimer = () => {
    pauseTiming();
    drillTimer.pause();
    // Pause session timer when drill timer is paused
    setSessionTimerRunning(false);
    trackEvent(EVENTS.DRILL_TIMER_PAUSED, { drillId: currentDrill.id });
  };
  
  const calculateTotalSessionTime = (drillsArray = null) => {
    const drills = drillsArray || completedDrills;
    const totalDuration = drills.reduce((sum, d) => sum + (d.durationSeconds || 0), 0);
    setSessionTimer({ seconds: totalDuration });
    return totalDuration;
  };
  
  const isVideoCaptureSupported = () => {
    return true;
  };
  
  const isWebShareSupported = () => {
    const hasShare = !!navigator.share;
    const hasCanShare = !!navigator.canShare;
    const isFunction = typeof navigator.share === 'function';
    const isSecureContext = window.isSecureContext;
    const protocol = window.location.protocol;
    const isHTTPS = protocol === 'https:' || protocol === 'https';
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const result = hasShare && hasCanShare && isFunction;
    
    console.log('[Video Share] Web Share API support check:', {
      hasShare,
      hasCanShare,
      isFunction,
      result,
      isSecureContext,
      protocol,
      isHTTPS,
      isLocalhost,
      hostname: window.location.hostname,
      navigatorShare: navigator.share,
      navigatorCanShare: navigator.canShare,
      userAgent: navigator.userAgent?.substring(0, 100)
    });
    
    try {
      const inIframe = window.self !== window.top;
      const standalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
      console.log('[Video Share] Context check:', {
        inIframe,
        standalone,
        displayMode: window.matchMedia('(display-mode: standalone)').matches
      });
    } catch (e) {
      console.log('[Video Share] Context check failed:', e.message);
    }
    
    return result;
  };
  
  // Handle video file selection
  const handleVideoFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Check if it's a video file
    if (!file.type.startsWith('video/')) {
      showModal(
        t('training.video_capture_not_supported'),
        t('training.video_capture_not_supported'),
        null,
        'alert',
        'warning'
      );
      return;
    }
    
    setIsProcessingVideo(true);
    
    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setVideoPreviewUrl(previewUrl);
      setVideoFile(file);
      
      // Extract video duration
      const duration = await extractVideoDuration(file);
      if (duration > 0) {
        setVideoDuration(Math.round(duration));
        
        // Set session start time if this is the first drill
        if (completedDrills.length === 0 && !sessionStartTime) {
          setSessionStartTime(new Date().toISOString());
        }
      } else {
        showModal(
          t('training.video_duration_error'),
          t('training.video_duration_error'),
          null,
          'alert',
          'warning'
        );
      }
    } catch (error) {
      console.error('Error processing video:', error);
      showModal(
        t('error.error_occurred'),
        error.message || t('error.unknown_error'),
        null,
        'alert',
        'danger'
      );
      // Reset video state on error
      setVideoFile(null);
      setVideoPreviewUrl(null);
      setVideoDuration(0);
    } finally {
      setIsProcessingVideo(false);
    }
  };
  
  // Extract video duration from file
  const extractVideoDuration = (file) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  };
  
  // Trigger video capture
  const handleRecordVideo = () => {
    if (!isVideoCaptureSupported()) {
      showModal(
        t('training.video_capture_not_supported'),
        t('training.video_capture_not_supported'),
        null,
        'alert',
        'warning'
      );
      return;
    }
    
    if (videoInputRef.current) {
      videoInputRef.current.click();
    }
  };
  
  // Cancel video recording session
  const handleCancelVideo = () => {
    // Clean up video preview URL
    if (videoPreviewUrl) {
      URL.revokeObjectURL(videoPreviewUrl);
    }
    
    // Reset video state
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoDuration(0);
    
    // Reset video input
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
    
    // Return to drill list
    setCurrentDrill(null);
    setDrillNotes('');
    setSelectedFocusIds([]);
    stopTiming();
    drillTimer.reset();
  };
  
  // Core finishing logic (extracted to be reusable)
  const proceedFinishVideo = async (shouldShare = false) => {
    // Save video file reference for sharing before cleaning up
    const fileToShare = videoFile;
    const currentPreviewUrl = videoPreviewUrl;
    
    // Preserve originDurationSeconds if it was set (drill was edited before)
    const originDurationSeconds = currentDrill._originDurationSeconds;
    const cleanDrill = { ...currentDrill };
    delete cleanDrill._originDurationSeconds;
    
    // Create completed drill with video duration
    const completedDrill = {
      drillId: currentDrill.id,
      drillName: currentDrill[`name${i18n.language === 'he' ? 'He' : 'En'}`],
      durationSeconds: videoDuration,
      notes: drillNotes.trim() || null,
      focus: selectedFocusIds.length > 0 ? [...selectedFocusIds] : [],
      ...(originDurationSeconds !== undefined && { originDurationSeconds }),
      drill: cleanDrill
    };
    
    const updatedCompletedDrills = [...completedDrills, completedDrill];
    setCompletedDrills(updatedCompletedDrills);

    // Shuriken toast for video drill
    {
      const rewardConfig = { ...DEFAULT_REWARD_CONFIG, ...(schoolData?.rewardConfig || {}) };
      const multiplier = calculateMultiplier(userData?.stats?.currentStreak || 0);
      const isFirstEverDrill = !userData?.stats?.hasCompletedFirstDrill && completedDrills.length === 0;
      const prevSessionSeconds = completedDrills.reduce((sum, d) => sum + d.durationSeconds, 0);
      const newSessionSeconds = prevSessionSeconds + completedDrill.durationSeconds;
      const drillShurikens = calculateDrillShurikens(completedDrill.durationSeconds, multiplier, isFirstEverDrill, rewardConfig);
      const bonusShurikens = calculateSessionBonusShurikens(prevSessionSeconds, newSessionSeconds, multiplier, rewardConfig);
      const totalEarned = drillShurikens + bonusShurikens;
      const isGold = multiplier > 1.0 || bonusShurikens > 0;
      setLastDrillShurikens({ count: totalEarned, isGold });
      setTimeout(() => setLastDrillShurikens(null), 3000);
    }

    setSessionTimerRunning(false);

    // Calculate and update total session time
    calculateTotalSessionTime(updatedCompletedDrills);
    
    // Clean up video preview URL
    if (currentPreviewUrl) {
      URL.revokeObjectURL(currentPreviewUrl);
    }
    
    // Reset video state
    setVideoFile(null);
    setVideoPreviewUrl(null);
    setVideoDuration(0);
    
    // Reset video input
    if (videoInputRef.current) {
      videoInputRef.current.value = '';
    }
    
    // Handle navigation and auto-save (similar to handleFinishDrill)
    if (activeCustomList) {
      const nextIndex = activeCustomList.currentIndex + 1;
      
      if (nextIndex < activeCustomList.drillIds.length) {
        (async () => {
          try {
            const nextDrillId = activeCustomList.drillIds[nextIndex];
            const nextDrill = await schoolsDB.getDrill(userData.profile.schoolId, nextDrillId);
            
            if (nextDrill) {
              setActiveCustomList(prev => ({
                ...prev,
                currentIndex: nextIndex
              }));
              setCurrentDrill(nextDrill);
              setDrillNotes('');
              setSelectedFocusIds([]);
              stopTiming();
              drillTimer.reset();
            } else {
              setActiveCustomList(null);
              setCurrentDrill(null);
            }
          } catch (err) {
            console.error('Error loading next drill:', err);
            setActiveCustomList(null);
            setCurrentDrill(null);
          }
        })();
      } else {
        setActiveCustomList(null);
        setCurrentDrill(null);
        setDrillNotes('');
        setSelectedFocusIds([]);
        stopTiming();
        drillTimer.reset();
      }
    } else {
      setCurrentDrill(null);
      setDrillNotes('');
      setSelectedFocusIds([]);
      stopTiming();
      drillTimer.reset();
    }
    
    // Stop session timer
    setSessionTimerRunning(false);
    
    trackEvent(EVENTS.DRILL_COMPLETED, {
      drillId: completedDrill.drillId,
      durationSeconds: completedDrill.durationSeconds,
      source: 'video'
    });
    
    // Auto-save the session after drill completion
    if (autoSaveInProgressRef.current) {
      if (shouldShare) {
        // Share after auto-save completes
        setTimeout(() => handleShareVideo(fileToShare), 1000);
      }
      return;
    }
    
    autoSaveInProgressRef.current = true;
    (async () => {
      try {
        const totalDuration = updatedCompletedDrills.reduce((sum, d) => sum + d.durationSeconds, 0);
        const drillsForDB = updatedCompletedDrills.map(({ drill, ...drillData }) => drillData);
        
        await saveSession({
          drills: drillsForDB,
          totalDurationSeconds: totalDuration,
          startedAt: sessionStartTime,
          sessionNotes: sessionNotes.trim() || null
        });
        
        // Share video if requested
        if (shouldShare) {
          await handleShareVideo(fileToShare);
        }
      } catch (err) {
        console.error('Error auto-saving session:', err);
        trackEvent(EVENTS.ERROR_OCCURRED, {
          errorType: 'auto_save_session',
          errorMessage: err.message
        });
        
        // Still try to share if requested, even if save failed
        if (shouldShare) {
          await handleShareVideo(fileToShare);
        }
      } finally {
        autoSaveInProgressRef.current = false;
      }
    })();
  };
  
  // Finish video recording session (award XP, update streak)
  const handleFinishVideo = async (shouldShare = false) => {
    if (!videoFile || videoDuration === 0) {
      showModal(t('training.no_time_error'), t('training.no_time_error_message') || '', null, 'alert', 'warning');
      return;
    }
    
    // For custom drills, notes are required
    if (currentDrill.id === 'custom' && !drillNotes.trim()) {
      showModal(t('training.notes_required'), t('training.notes_required_message') || '', null, 'alert', 'warning');
      return;
    }
    
    // If not sharing, show confirmation dialog
    if (!shouldShare) {
      showModal(
        t('training.finish_without_share_title'),
        t('training.finish_without_share_message'),
        () => proceedFinishVideo(false),
        'confirm',
        'warning'
      );
      return;
    }
    
    // Proceed directly if sharing
    proceedFinishVideo(shouldShare);
  };
  
  const handleShareVideo = async (fileToShare) => {
    const drillName = currentDrill ? currentDrill[`name${i18n.language === 'he' ? 'He' : 'En'}`] : '';
    const shareText = t('training.video_share_text', { drillName });
    
    console.log('[Video Share] Starting share process...');
    console.log('[Video Share] File details:', {
      name: fileToShare?.name,
      size: fileToShare?.size,
      type: fileToShare?.type,
      lastModified: fileToShare?.lastModified
    });
    
    const shareSupported = isWebShareSupported();
    console.log('[Video Share] Web Share API support check:', {
      shareSupported,
      navigatorShare: typeof navigator.share,
      navigatorCanShare: typeof navigator.canShare,
      userAgent: navigator.userAgent
    });
    
    if (!shareSupported) {
      const isSecureContext = window.isSecureContext;
      const protocol = window.location.protocol;
      const isHTTPS = protocol === 'https:' || protocol === 'https';
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      
      console.warn('[Video Share] Web Share API not supported on this device');
      console.log('[Video Share] Diagnostic information:', {
        navigatorShare: navigator.share,
        navigatorCanShare: navigator.canShare,
        hasShare: 'share' in navigator,
        hasCanShare: 'canShare' in navigator,
        isSecureContext,
        protocol,
        isHTTPS,
        isLocalhost,
        hostname: window.location.hostname,
        userAgent: navigator.userAgent
      });
      
      let errorMessage = t('training.web_share_not_supported');
      if (!isSecureContext && !isLocalhost) {
        console.warn('[Video Share] ⚠️ Web Share API requires HTTPS. Current protocol:', protocol);
        errorMessage = `${errorMessage}\n\nWeb Share API requires a secure connection (HTTPS). Current: ${protocol}//${window.location.hostname}`;
      }
      
      showModal(
        t('training.web_share_not_supported'),
        errorMessage,
        null,
        'alert',
        'warning'
      );
      return;
    }
    
    try {
      const canShareFiles = navigator.canShare && navigator.canShare({ files: [fileToShare] });
      console.log('[Video Share] Can share files check:', {
        canShareFiles,
        canShareExists: !!navigator.canShare,
        canShareResult: navigator.canShare ? navigator.canShare({ files: [fileToShare] }) : 'N/A'
      });
      
      if (canShareFiles) {
        console.log('[Video Share] Attempting to share with file...');
        const shareData = {
          files: [fileToShare],
          title: `${drillName} - Training Video`,
          text: shareText
        };
        console.log('[Video Share] Share data:', {
          ...shareData,
          files: shareData.files.map(f => ({ name: f.name, type: f.type, size: f.size }))
        });
        
        await navigator.share(shareData);
        console.log('[Video Share] ✅ Share successful with file');
      } else {
        console.log('[Video Share] Cannot share files, falling back to sharing without file...');
        const shareDataWithoutFile = {
          title: `${drillName} - Training Video`,
          text: shareText
        };
        console.log('[Video Share] Share data (without file):', shareDataWithoutFile);
        
        await navigator.share(shareDataWithoutFile);
        console.log('[Video Share] ✅ Share successful without file');
      }
    } catch (error) {
      console.error('[Video Share] ❌ Error during share:', {
        name: error.name,
        message: error.message,
        stack: error.stack,
        error: error
      });
      
      if (error.name === 'AbortError') {
        console.log('[Video Share] User cancelled the share');
      } else {
        console.error('[Video Share] Non-cancellation error occurred:', {
          errorName: error.name,
          errorMessage: error.message,
          errorStack: error.stack,
          navigatorShare: typeof navigator.share,
          navigatorCanShare: typeof navigator.canShare
        });
      }
    }
  };
  
  // Cleanup video preview URL on unmount
  useEffect(() => {
    return () => {
      if (videoPreviewUrl) {
        URL.revokeObjectURL(videoPreviewUrl);
      }
    };
  }, [videoPreviewUrl]);
  
  const handleFinishDrill = async () => {
    if (drillTimer.elapsedSeconds === 0) {
      showModal(t('training.no_time_error'), t('training.no_time_error_message') || '', null, 'alert', 'warning');
      return;
    }
    
    // For custom drills, notes are required
    if (currentDrill.id === 'custom' && !drillNotes.trim()) {
      showModal(t('training.notes_required'), t('training.notes_required_message') || '', null, 'alert', 'warning');
      return;
    }
    
    // Preserve originDurationSeconds if it was set (drill was edited before)
    // Remove the metadata prefix when storing in completedDrill
    const originDurationSeconds = currentDrill._originDurationSeconds;
    const cleanDrill = { ...currentDrill };
    delete cleanDrill._originDurationSeconds;
    
    const completedDrill = {
      drillId: currentDrill.id,
      drillName: currentDrill[`name${i18n.language === 'he' ? 'He' : 'En'}`],
      durationSeconds: drillTimer.elapsedSeconds,
      notes: drillNotes.trim() || null,
      focus: selectedFocusIds.length > 0 ? [...selectedFocusIds] : [],
      // Preserve originDurationSeconds if drill was previously edited
      ...(originDurationSeconds !== undefined && { originDurationSeconds }),
      // Store the full drill object so we can restart it (without metadata)
      drill: cleanDrill
    };
    
    const updatedCompletedDrills = [...completedDrills, completedDrill];
    setCompletedDrills(updatedCompletedDrills);

    // Calculate shurikens earned for this drill (for immediate UI feedback)
    {
      const rewardConfig = { ...DEFAULT_REWARD_CONFIG, ...(schoolData?.rewardConfig || {}) };
      const currentStreak = userData?.stats?.currentStreak || 0;
      const multiplier = calculateMultiplier(currentStreak);
      const isFirstEverDrill = !userData?.stats?.hasCompletedFirstDrill && completedDrills.length === 0;
      const prevSessionSeconds = completedDrills.reduce((sum, d) => sum + d.durationSeconds, 0);
      const newSessionSeconds = prevSessionSeconds + completedDrill.durationSeconds;
      const drillShurikens = calculateDrillShurikens(completedDrill.durationSeconds, multiplier, isFirstEverDrill, rewardConfig);
      const bonusShurikens = calculateSessionBonusShurikens(prevSessionSeconds, newSessionSeconds, multiplier, rewardConfig);
      const totalEarned = drillShurikens + bonusShurikens;
      const isGold = multiplier > 1.0 || bonusShurikens > 0;
      setLastDrillShurikens({ count: totalEarned, isGold });
      // Auto-dismiss after 3 seconds
      setTimeout(() => setLastDrillShurikens(null), 3000);
    }

    // Check if we're in a custom list and should auto-advance
    if (activeCustomList) {
      const nextIndex = activeCustomList.currentIndex + 1;
      
      if (nextIndex < activeCustomList.drillIds.length) {
        // More drills in list - auto-select next drill
        (async () => {
          try {
            const nextDrillId = activeCustomList.drillIds[nextIndex];
            const nextDrill = await schoolsDB.getDrill(userData.profile.schoolId, nextDrillId);
            
            if (nextDrill) {
              setActiveCustomList(prev => ({
                ...prev,
                currentIndex: nextIndex
              }));
              setCurrentDrill(nextDrill);
              setDrillNotes('');
              setSelectedFocusIds([]);
              stopTiming();
              drillTimer.reset();
            } else {
              // Drill not found - clear active list
              setActiveCustomList(null);
              setCurrentDrill(null);
            }
          } catch (err) {
            console.error('Error loading next drill:', err);
            setActiveCustomList(null);
            setCurrentDrill(null);
          }
        })();
      } else {
        // List complete
        setActiveCustomList(null);
        setCurrentDrill(null);
        setDrillNotes('');
        setSelectedFocusIds([]);
        stopTiming();
        drillTimer.reset();
      }
    } else {
      // Normal flow - no active list
      setCurrentDrill(null);
      setDrillNotes('');
      setSelectedFocusIds([]);
      stopTiming();
      drillTimer.reset();
    }
    
    // Stop session timer when finishing a drill
    setSessionTimerRunning(false);
    
    trackEvent(EVENTS.DRILL_COMPLETED, {
      drillId: completedDrill.drillId,
      durationSeconds: completedDrill.durationSeconds
    });
    
    // Auto-save the session after drill completion
    // Use a ref to prevent race conditions if drills are completed quickly
    if (autoSaveInProgressRef.current) {
      return; // Skip if auto-save is already in progress
    }
    
    autoSaveInProgressRef.current = true;
    (async () => {
      try {
        const totalDuration = updatedCompletedDrills.reduce((sum, d) => sum + d.durationSeconds, 0);
        
        // Remove the drill object before saving to database (only needed for UI)
        const drillsForDB = updatedCompletedDrills.map(({ drill, ...drillData }) => drillData);
        
        // Creates the session doc on first save, updates it afterwards
        await saveSession({
          drills: drillsForDB,
          totalDurationSeconds: totalDuration,
          startedAt: sessionStartTime,
          sessionNotes: sessionNotes.trim() || null
        });
      } catch (err) {
        console.error('Error auto-saving session:', err);
        // Don't show error to user - this is a background operation
        trackEvent(EVENTS.ERROR_OCCURRED, {
          errorType: 'auto_save_session',
          errorMessage: err.message
        });
      } finally {
        autoSaveInProgressRef.current = false;
      }
    })();
  };
  
  const handleContinueDrill = (completedDrill, drillIndex) => {
    if (!completedDrill.drill) {
      // If drill object is not stored, we need to fetch it
      // For now, we'll just log an error (this shouldn't happen if we're storing it)
      console.error('Drill object not found in completed drill');
      return;
    }
    
    // Remove the drill from completed list so we can continue it
    setCompletedDrills(prev => prev.filter((_, idx) => idx !== drillIndex));
    
    // Resume the drill from where it left off
    // Preserve originDurationSeconds and edited status by attaching it to the drill object
    const drillWithMetadata = {
      ...completedDrill.drill,
      _originDurationSeconds: completedDrill.originDurationSeconds, // Preserve edited status
    };
    
    stopTiming();
    setCurrentDrill(drillWithMetadata);
    setDrillNotes(completedDrill.notes || '');
    setSelectedFocusIds(completedDrill.focus || []);
    drillTimer.pause();
    drillTimer.setElapsed(completedDrill.durationSeconds || 0);
    
    trackEvent(EVENTS.DRILL_SELECTED, { 
      drillId: completedDrill.drill.id, 
      drillName: completedDrill.drill[`name${i18n.language === 'he' ? 'He' : 'En'}`],
      source: 'continue'
    });
  };
  
  const handleEditDrill = (drill, drillIndex) => {
    const minutes = Math.floor(drill.durationSeconds / 60);
    const seconds = drill.durationSeconds % 60;
    const selectedFocusIds = drill.focus || [];
    setEditDrillState({ isOpen: true, drillIndex, minutes, seconds, selectedFocusIds });
  };
  
  const handleCloseEditDrill = () => {
    setEditDrillState({ isOpen: false, drillIndex: null, minutes: 0, seconds: 0, selectedFocusIds: [] });
  };
  
  const handleSaveDrillEdit = () => {
    const { drillIndex, minutes, seconds, selectedFocusIds } = editDrillState;
    if (drillIndex === null) return;
    
    const newDurationSeconds = minutes * 60 + seconds;
    if (newDurationSeconds <= 0) {
      showModal(
        t('training.edit_drill_invalid_time'),
        t('training.edit_drill_invalid_time_message'),
        null,
        'alert',
        'warning'
      );
      return;
    }
    
    const oldDuration = completedDrills[drillIndex].durationSeconds;
    
    setCompletedDrills(prev => {
      const updated = prev.map((drill, idx) => {
        if (idx === drillIndex) {
          // First edit: set originDurationSeconds to current durationSeconds
          // Subsequent edits: preserve originDurationSeconds
          const originDurationSeconds = drill.originDurationSeconds !== undefined 
            ? drill.originDurationSeconds 
            : drill.durationSeconds;
          
          return {
            ...drill,
            durationSeconds: newDurationSeconds,
            originDurationSeconds,
            focus: selectedFocusIds.length > 0 ? [...selectedFocusIds] : []
          };
        }
        return drill;
      });
      
      // Update session timer with new total time
      const totalDuration = updated.reduce((sum, d) => sum + d.durationSeconds, 0);
      setSessionTimer({ seconds: totalDuration });
      
      return updated;
    });
    
    handleCloseEditDrill();
    trackEvent(EVENTS.DRILL_EDITED, { 
      drillId: completedDrills[drillIndex].drillId,
      oldDuration,
      newDuration: newDurationSeconds
    });
  };
  
  const handleDeleteDrill = () => {
    const { drillIndex } = editDrillState;
    if (drillIndex === null) return;
    
    // Capture drill info before deletion
    const drillToDelete = completedDrills[drillIndex];
    
    showModal(
      t('training.delete_drill_confirm'),
      t('training.delete_drill_confirm_message'),
      () => {
        setCompletedDrills(prev => {
          const updated = prev.filter((_, idx) => idx !== drillIndex);
          // Update session timer with new total time
          const totalDuration = updated.reduce((sum, d) => sum + d.durationSeconds, 0);
          setSessionTimer({ seconds: totalDuration });
          return updated;
        });
        handleCloseEditDrill();
        trackEvent(EVENTS.DRILL_DELETED, { 
          drillId: drillToDelete.drillId
        });
      },
      'confirm',
      'danger'
    );
  };
  
  const handleFinishTraining = async () => {
    if (completedDrills.length === 0) {
      showModal(t('training.no_drills_error'), t('training.no_drills_error_message') || '', null, 'alert', 'warning');
      return;
    }

    setSaving(true);

    const totalDuration = completedDrills.reduce((sum, d) => sum + d.durationSeconds, 0);
    const drillsForDB = completedDrills.map(({ drill, ...drillData }) => drillData);

    const isOffline = !navigator.onLine;

    if (isOffline) {
      // Offline: Firestore write promises never resolve, so don't await.
      // Fire the write (it queues locally and will sync when online), then navigate immediately.
      saveSession({
        drills: drillsForDB,
        totalDurationSeconds: totalDuration,
        startedAt: sessionStartTime,
        sessionNotes: sessionNotes.trim() || null
      }).catch((err) => console.error('Offline write (will retry when online):', err));
      const result = computeOptimisticResult(userData);

      // Optimistic shuriken calculation for offline display
      const rewardConfig = { ...DEFAULT_REWARD_CONFIG, ...(schoolData?.rewardConfig || {}) };
      const multiplier = calculateMultiplier(result.newStreak);
      let optimisticShurikens = 0;
      let prevSecs = 0;
      const isFirstEver = !userData?.stats?.hasCompletedFirstDrill;
      drillsForDB.forEach((drill, i) => {
        const isFirst = isFirstEver && i === 0;
        optimisticShurikens += calculateDrillShurikens(drill.durationSeconds, multiplier, isFirst, rewardConfig);
        const newSecs = prevSecs + drill.durationSeconds;
        optimisticShurikens += calculateSessionBonusShurikens(prevSecs, newSecs, multiplier, rewardConfig);
        prevSecs = newSecs;
      });

      sessionStorage.clear();
      setSessionId(null);
      setSaving(false);
      navigate('/training-success', {
        state: {
          totalDuration,
          drillCount: completedDrills.length,
          newStreak: result.newStreak,
          milestoneReached: result.milestoneReached,
          pendingSync: true,
          sessionShurikens: optimisticShurikens,
        }
      });
      return;
    }

    try {
      let result;

      result = await saveSession({
        drills: drillsForDB,
        totalDurationSeconds: totalDuration,
        startedAt: sessionStartTime,
        sessionNotes: sessionNotes.trim() || null
      });
      if (!result) {
        // Session doc already existed (auto-saved) - this was an update
        const userDataResult = await usersDB.getUserData(currentUser.uid);
        result = {
          newStreak: userDataResult.stats.currentStreak,
          streakIncremented: false,
          milestoneReached: null,
          dailyGoalReached: false
        };
      }

      // Award shurikens for the full session (non-blocking — don't let this prevent navigation)
      let shurikanResult = { sessionShurikens: 0, newTotalShurikens: 0, newWeeklyShurikens: 0 };
      try {
        shurikanResult = await trainingsDB.awardShurikens(currentUser.uid, drillsForDB);
      } catch (shurikanErr) {
        console.error('Error awarding shurikens:', shurikanErr);
      }

      // Overtake check — fire-and-forget, result used for on-screen rank congrats
      let newRank = null;
      const schoolId = userData?.profile?.schoolId;
      if (schoolId) {
        const overtakeResult = await callOvertake(currentUser.uid, schoolId);
        newRank = overtakeResult.newRank;
      }

      trackEvent(EVENTS.TRAINING_SESSION_COMPLETED, {
        totalDuration,
        drillCount: completedDrills.length
      });

      if (result.streakIncremented) {
        trackEvent(EVENTS.STREAK_INCREMENTED, { newStreak: result.newStreak });
      }

      if (result.milestoneReached) {
        trackEvent(EVENTS.STREAK_MILESTONE, { milestone: result.milestoneReached });
      }

      if (result.dailyGoalReached) {
        trackEvent(EVENTS.DAILY_GOAL_REACHED);
      }

      sessionStorage.clear();
      setSessionId(null);

      navigate('/training-success', {
        state: {
          totalDuration,
          drillCount: completedDrills.length,
          newStreak: result.newStreak,
          milestoneReached: result.milestoneReached,
          sessionShurikens: shurikanResult.sessionShurikens,
          newTotalShurikens: shurikanResult.newTotalShurikens,
          newWeeklyShurikens: shurikanResult.newWeeklyShurikens,
          newRank,
        }
      });
    } catch (err) {
      console.error('Error finishing training:', err);
      trackEvent(EVENTS.ERROR_OCCURRED, {
        errorType: 'finish_training',
        errorMessage: err.message
      });
      showModal(t('error.server_error'), err.message, null, 'alert', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const computeOptimisticResult = (data) => {
    if (!data?.stats) {
      return { newStreak: 1, milestoneReached: null };
    }
    const { newStreak } = computeStreakUpdate(data.stats, schoolDate(new Date()));
    const milestones = [7, 14, 30, 60, 100];
    const milestoneReached = milestones.includes(newStreak) ? newStreak : null;
    return { newStreak, milestoneReached };
  };
  
  const formatSessionTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  
  if (loading) {
    return <LoadingSpinner message={t('common.loading')} />;
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
        <div className="max-w-2xl mx-auto">
          <ErrorMessage 
            error={error} 
            onRetry={() => window.location.reload()}
            className="mt-8"
          />
        </div>
      </div>
    );
  }
  
  if (!userData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
        <div className="max-w-2xl mx-auto">
          <ErrorMessage 
            error={{ message: t('error.not_found') }} 
            onRetry={() => window.location.reload()}
            className="mt-8"
          />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/20 p-4">
      {/* Shuriken earned toast */}
      {lastDrillShurikens && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
          <div className="flex items-center gap-2 bg-white rounded-2xl shadow-2xl px-5 py-3 border border-yellow-200">
            <img
              src={lastDrillShurikens.isGold ? '/shurikan-gold.png' : '/shurikan-silver.png'}
              alt="shuriken"
              className="w-12 h-12 object-contain"
            />
            <span className="font-bold text-xl text-gray-800">+{lastDrillShurikens.count}</span>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* Header with Back Button */}
        <div className="flex items-center gap-4 mb-4 md:mb-10">
          <button
            onClick={() => navigate('/home')}
            className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
            title={t('common.back')}
          >
            {i18n.language === 'he' ? '→' : '←'}
          </button>
          <h1 className="text-xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 to-accent-500 bg-clip-text text-transparent flex-1">
            {t('training.training_session')}
          </h1>
        </div>
        
        {/* Session Timer - Reduced height 20%: 80px total (not 120px) */}
        <div className="bg-white rounded-2xl p-4 md:p-10 mb-4 md:mb-10 shadow-xl animate-slide-up">
          <div className="text-center">
            <div className="text-gray-600 text-sm md:text-2xl font-semibold mb-2 md:mb-6">{t('training.total_time')}</div>
            <div className="text-3xl md:text-8xl font-mono font-bold text-primary-600">
              {formatSessionTime(sessionTimer.seconds)}
            </div>
          </div>
        </div>
        
        {/* Current Drill - Reduced height 20%: drill info 100px (not 140px) */}
        {currentDrill ? (
          <div className="bg-white rounded-2xl p-4 md:p-10 mb-4 md:mb-10 shadow-xl animate-slide-up">
            {videoFile ? (
              /* Screen 2: Recording Complete */
              <div className="text-center">
                <div className="text-gray-600 text-sm md:text-2xl font-semibold mb-2 md:mb-4">
                  {t('training.current_drill')}
                </div>
                <div className="text-xl md:text-4xl font-bold mb-4 md:mb-8 text-primary-700 flex items-center justify-center gap-2 md:gap-3">
                  {currentDrill[`name${i18n.language === 'he' ? 'He' : 'En'}`]}
                </div>
                
                {/* Video Preview */}
                {videoPreviewUrl && (
                  <div className="mb-4 md:mb-8">
                    <video
                      ref={videoElementRef}
                      src={videoPreviewUrl}
                      className="w-full max-h-[200px] mx-auto rounded-2xl shadow-lg object-cover"
                      controls={false}
                      playsInline
                      preload="metadata"
                      onLoadedMetadata={(e) => {
                        // Ensure video is paused on first frame
                        e.target.currentTime = 0;
                        e.target.pause();
                      }}
                    />
                  </div>
                )}
                
                {/* Training Duration Display */}
                <div className="text-2xl md:text-4xl font-bold mb-4 md:mb-8 text-primary-600">
                  {t('training.trained_seconds', { seconds: videoDuration })}
                </div>
                
                {isProcessingVideo && (
                  <div className="text-gray-600 text-sm md:text-xl mb-4 md:mb-8">
                    {t('training.processing_video')}
                  </div>
                )}
                
                {/* Action Buttons */}
                <div className="flex flex-col gap-2 md:gap-4 justify-center mb-4 md:mb-10">
                  <button
                    onClick={handleCancelVideo}
                    disabled={isProcessingVideo}
                    className="w-full px-6 md:px-10 py-4 md:py-6 bg-gray-500 text-white rounded-2xl font-bold hover:bg-gray-600 text-lg md:text-2xl min-h-[52px] md:min-h-[80px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {t('common.cancel')}
                  </button>
                  
                  <button
                    onClick={() => handleFinishVideo(false)}
                    disabled={isProcessingVideo || videoDuration === 0}
                    className="w-full px-6 md:px-10 py-4 md:py-6 btn-primary text-white rounded-2xl font-bold text-lg md:text-2xl min-h-[52px] md:min-h-[80px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {t('common.finish')}
                  </button>
                  
                  <button
                    onClick={() => handleFinishVideo(true)}
                    disabled={isProcessingVideo || videoDuration === 0}
                    className="w-full px-6 md:px-10 py-4 md:py-6 bg-success-600 text-white rounded-2xl font-bold hover:bg-success-700 text-lg md:text-2xl min-h-[52px] md:min-h-[80px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {t('training.finish_and_share')}
                  </button>
                </div>
                
                {/* Note about video saved */}
                <div className="text-xs md:text-sm text-gray-500 italic">
                  {t('training.video_saved_note')}
                </div>
              </div>
            ) : (
              /* Screen 1: Drill Ready */
              <div className="text-center mb-4 md:mb-10">
                <div className="text-gray-600 text-sm md:text-2xl font-semibold mb-2 md:mb-4">
                  {t('training.current_drill')}
                </div>
                <div className="text-xl md:text-4xl font-bold mb-2 md:mb-8 text-primary-700 flex items-center justify-center gap-2 md:gap-3">
                  {currentDrill[`name${i18n.language === 'he' ? 'He' : 'En'}`]}
                </div>

                {/* Reference video — shown above the timer when the drill has one */}
                {currentDrill?.video && (
                  <div className="mb-4 md:mb-8">
                    <DrillVideoPlayer
                      drill={currentDrill}
                      schoolId={userData?.profile?.schoolId}
                    />
                  </div>
                )}

                <div className="text-4xl md:text-8xl font-mono font-bold mb-3 md:mb-8 text-primary-600">
                  {drillTimer.formattedTime}
                </div>
                
                {drillTimer.isRunning && (
                  <div className="text-red-600 text-sm md:text-2xl mb-3 md:mb-8 animate-pulse-slow">
                    🔴 {t('training.recording')}
                  </div>
                )}
                
                {/* Wake Lock Indicator */}
                {isWakeLockActive && isWakeLockSupported && (
                  <div className="text-green-600 text-xs md:text-lg mb-2 md:mb-4 flex items-center justify-center gap-1 md:gap-2">
                    <span>🔋</span>
                    <span className="opacity-75">{t('training.screen_stay_on')}</span>
                  </div>
                )}
                
                {currentDrill?.timings?.length ? (
                  <div className={`mb-4 md:mb-6 rounded-xl border ${timingEnabled ? 'border-primary-200 bg-primary-50' : 'border-gray-200 bg-gray-100'}`}>
                    <label className="flex flex-wrap items-center gap-3 text-sm md:text-xl font-semibold text-gray-700 px-3 py-2 md:px-4 md:py-3 transition-opacity">
                      <input
                        type="checkbox"
                        className="h-5 w-5 accent-primary-500"
                        checked={timingEnabled}
                        disabled={drillTimer.isRunning || isCountdownActive}
                        onChange={(event) => {
                          setTimingEnabled(event.target.checked);
                        }}
                      />
                      <span>{t('training.time_me')}:</span>
                        <span className={`flex flex-wrap items-center gap-2 ${!timingEnabled ? 'opacity-50' : ''}`}>
                        {currentDrill.timings.map((phase, index) => {
                          const phaseName = phase[`name${i18n.language === 'he' ? 'He' : 'En'}`];
                          const isLast = index === currentDrill.timings.length - 1;
                          return (
                            <span key={`${phaseName}-${index}`} className="flex items-center gap-1">
                              <span>{phaseName}</span>
                              <input
                                type="number"
                                min="1"
                                className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-center text-sm md:text-lg focus:outline-none focus:ring-2 focus:ring-primary-400 disabled:bg-gray-100"
                                value={timingValues[index] ?? ''}
                                disabled={!timingEnabled || drillTimer.isRunning || isCountdownActive}
                                onChange={(event) => handleTimingValueChange(index, event.target.value)}
                              />
                              <span>{t('common.seconds_short') || 's'}{!isLast ? ',' : ''}</span>
                            </span>
                          );
                        })}
                      </span>
                    </label>
                  </div>
                ) : null}
                
                <div className="flex flex-col gap-2 md:gap-4 justify-center mb-4 md:mb-10">
                  {!drillTimer.isRunning ? (
                    <button
                      onClick={handleStartTimer}
                      disabled={isCountdownActive}
                      className="w-full px-6 md:px-10 py-4 md:py-10 bg-success-600 text-white rounded-2xl font-bold hover:bg-success-700 text-lg md:text-3xl min-h-[52px] md:min-h-[100px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {drillTimer.elapsedSeconds === 0 ? t('common.start') : t('training.resume')}
                    </button>
                  ) : (
                    <button
                      onClick={handlePauseTimer}
                      className="w-full px-6 md:px-10 py-4 md:py-10 bg-accent-600 text-white rounded-2xl font-bold hover:bg-accent-700 text-lg md:text-3xl min-h-[52px] md:min-h-[100px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
                    >
                      {t('training.pause')}
                    </button>
                  )}
                  
                  {/* Record Video Button - Only show when timer is not running */}
                  {!drillTimer.isRunning && (
                    <button
                      onClick={handleRecordVideo}
                      disabled={isCountdownActive || isProcessingVideo}
                      className="w-full px-6 md:px-10 py-4 md:py-10 bg-primary-600 text-white rounded-2xl font-bold hover:bg-primary-700 text-lg md:text-3xl min-h-[52px] md:min-h-[100px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {t('training.record_video')}
                    </button>
                  )}
                  
                  {drillTimer.elapsedSeconds > 0 && (
                    <button
                      onClick={handleFinishDrill}
                      className="w-full px-6 md:px-10 py-4 md:py-10 btn-primary text-white rounded-2xl font-bold text-lg md:text-3xl min-h-[52px] md:min-h-[100px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
                    >
                      {t('common.finish')}
                    </button>
                  )}
                </div>
                
                {/* Note about sharing */}
                <div className="text-xs md:text-sm text-gray-500 italic mb-4 md:mb-6">
                  {t('training.video_share_note')}
                </div>
              
              <div>
                <label className="block text-sm md:text-xl font-semibold text-gray-700 mb-2 md:mb-4">
                  {currentDrill.id === 'custom' 
                    ? t('training.notes_required') 
                    : t('training.notes_optional')
                  }
                </label>
                <textarea
                  value={drillNotes}
                  onChange={(e) => setDrillNotes(e.target.value)}
                  className="input text-sm md:text-xl resize-none"
                  rows={2}
                  placeholder={currentDrill.id === 'custom' ? t('training.what_practiced') : ''}
                />
              </div>
              
                {/* Focus Selection */}
                <FocusSelector
                  applicableFocus={getApplicableFocus()}
                  selectedFocusIds={selectedFocusIds}
                  onFocusChange={setSelectedFocusIds}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6 mb-4 md:mb-10">
            {/* Custom Lists Dropdown */}
            {customLists.length > 0 && (
              <select
                value={activeCustomList?.listId || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    handleLoadCustomList(e.target.value);
                  } else {
                    setActiveCustomList(null);
                  }
                }}
                className="w-full px-4 md:px-6 py-3 md:py-4 border-2 border-gray-300 rounded-2xl font-semibold text-base md:text-xl bg-white hover:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400"
                style={{ direction: 'rtl' }}
              >
                <option value="">{t('custom_lists.select_list')} ▾</option>
                {customLists.map(list => (
                  <option key={list.listId} value={list.listId}>
                    {list.name}
                  </option>
                ))}
              </select>
            )}
            
            {/* List Progress Indicator */}
            {activeCustomList && (
              <div className="bg-gradient-to-r from-primary-500/10 to-primary-600/20 rounded-2xl p-4 md:p-6 text-center">
                <div className="text-sm md:text-xl font-semibold text-gray-700 mb-1">
                  {t('custom_lists.list_progress', {
                    name: activeCustomList.name,
                    current: activeCustomList.currentIndex + 1,
                    total: activeCustomList.drillIds.length
                  })}
                </div>
                {currentDrill && (
                  <div className="text-base md:text-2xl font-bold text-primary-700">
                    {currentDrill[`name${i18n.language === 'he' ? 'He' : 'En'}`]}
                  </div>
                )}
              </div>
            )}
            
            <button
              onClick={handleOpenDrillSelector}
              className="w-full btn-primary text-white py-4 md:py-10 rounded-2xl font-bold hover:shadow-xl text-lg md:text-3xl min-h-[52px] md:min-h-[100px] flex items-center justify-center hover:scale-105 active:scale-95 shadow-lg transition-all"
            >
              {t('training.select_drill')}
            </button>
          </div>
        )}

        {/* Drill Selector Modal - Add back button */}
        {showDrillSelector && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg p-4 md:p-10 max-w-md w-full max-h-[85vh] flex flex-col">
              <div className="flex items-center justify-between mb-4 flex-shrink-0">
                <button
                  onClick={handleCloseDrillSelector}
                  className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
                  title={t('common.close')}
                >
                  {i18n.language === 'he' ? '→' : '←'} {t('common.back')}
                </button>
                <h2 className="text-xl md:text-4xl font-bold">{t('drill_selector.title')}</h2>
                <div className="w-[60px]"></div>
              </div>
              <div className="overflow-y-auto flex-1 min-h-0">
                <DrillSelector
                  schoolId={userData.profile.schoolId}
                  schoolData={schoolData}
                  userBelt={userData.profile.currentBelt}
                  track={userData.profile.track || 'adult'}
                  onSelect={handleDrillSelected}
                  multiSelectMode={multiSelectMode}
                  customLists={customLists}
                  selectedDrills={selectedDrills}
                  editingListId={editingListId}
                  onToggleDrill={handleToggleDrill}
                  onLoadList={handleLoadListInSelector}
                  onSaveList={handleSaveList}
                  onDeleteList={handleDeleteList}
                  onClearSelections={handleClearSelections}
                  onReorderDrills={handleReorderDrills}
                />
              </div>
            </div>
          </div>
        )}
        
        {/* Completed Drills */}
        {completedDrills.length > 0 && (
          <div className="bg-white rounded-2xl p-4 md:p-10 mb-4 md:mb-10 shadow-xl animate-slide-up">
            <h3 className="font-bold mb-3 md:mb-8 text-lg md:text-3xl text-gray-800">
              {t('training.completed_drills')} ({completedDrills.length})
            </h3>
            <div className="space-y-2 md:space-y-4">
              {completedDrills.map((drill, idx) => (
                <CompletedDrillItem
                  key={idx}
                  drill={drill}
                  index={idx}
                  onEdit={handleEditDrill}
                  onContinue={handleContinueDrill}
                  continueDrillEnabled={drill.drill && !currentDrill}
                />
              ))}
            </div>
            
            {/* Session Notes - 80px height (not 120px) */}
            <div className="mt-4 md:mt-8 pt-3 md:pt-6 border-t border-gray-200">
              <label className="block text-sm md:text-xl font-semibold text-gray-700 mb-2 md:mb-4">
                {t('training.session_notes')}
              </label>
              <textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                className="input text-sm md:text-xl resize-none"
                rows={1}
                placeholder={t('training.session_notes_placeholder')}
              />
            </div>
          </div>
        )}
        
        {/* Recommendations */}
        <RecommendationCards
          recommendations={recommendations}
          onRecommendationClick={handleRecommendationClick}
          onSwipeLeft={handleSwipeLeft}
        />
        
        {/* Finish Training Button */}
        {completedDrills.length > 0 && !currentDrill && (
          <button
            onClick={handleFinishTraining}
            disabled={saving}
            className="w-full bg-gradient-to-r from-success-600 via-success-500 to-green-600 text-white py-4 md:py-10 rounded-2xl font-bold disabled:opacity-50 text-lg md:text-3xl min-h-[52px] md:min-h-[100px] flex items-center justify-center shadow-2xl hover:shadow-glow hover:scale-105 active:scale-95 transition-all"
          >
            {saving ? '...' : t('training.finish_training')}
          </button>
        )}
      </div>
      
      {isCountdownActive && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="text-white font-bold"
               style={{ fontSize: 'clamp(4rem, 30vmin, 12rem)' }}>
            {countdownDisplay}
          </div>
        </div>
      )}
      
      {/* Session Recovery Modal */}
      {showRecoveryModal && recoveryData && (
        <SessionRecoveryModal
          sessionData={recoveryData}
          onResume={handleResumeSession}
          onStartFresh={handleDiscardSession}
        />
      )}
      
      {/* Modal for alerts */}
      <Modal
        isOpen={modalState.isOpen}
        title={modalState.title}
        message={modalState.message}
        onConfirm={handleModalConfirm}
        onCancel={hideModal}
        type={modalState.type}
        variant={modalState.variant}
      />
      
      {/* Hidden Video Input */}
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleVideoFileSelect}
      />
      
      {/* Edit Drill Popup */}
      {editDrillState.isOpen && editDrillState.drillIndex !== null && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in"
          onClick={handleCloseEditDrill}
        >
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          
          {/* Popup content */}
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full transform animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-4 md:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg md:text-2xl font-bold text-gray-800">
                  {t('training.edit_drill')}
                </h3>
                <button
                  onClick={handleCloseEditDrill}
                  className="text-2xl hover:bg-gray-100 rounded-full p-2 transition-colors"
                  title={t('common.close')}
                >
                  ×
                </button>
              </div>
              {completedDrills[editDrillState.drillIndex] && (
                <p className="text-sm md:text-lg text-gray-600 mt-2">
                  {completedDrills[editDrillState.drillIndex].drillName}
                </p>
              )}
            </div>
            
            {/* Body */}
            <div className="px-4 md:px-6 py-4 md:py-6">
              <div className="mb-4 md:mb-6">
                <label className="block text-sm md:text-xl font-semibold text-gray-700 mb-2 md:mb-4">
                  {t('training.edit_drill_time')}
                </label>
                <div className="flex items-center gap-2 md:gap-4" style={{ direction: 'ltr' }}>
                  <div className="flex-1">
                    <label className="block text-xs md:text-sm text-gray-600 mb-1 md:mb-2 text-center">
                      {t('training.minutes')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="999"
                      value={editDrillState.minutes}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setEditDrillState(prev => ({ ...prev, minutes: Math.max(0, value) }));
                      }}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-300 rounded-lg text-center text-base md:text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                  <div className="pt-6 md:pt-8 text-xl md:text-3xl font-bold text-gray-400">:</div>
                  <div className="flex-1">
                    <label className="block text-xs md:text-sm text-gray-600 mb-1 md:mb-2 text-center">
                      {t('common.seconds_short')}
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="59"
                      value={editDrillState.seconds}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setEditDrillState(prev => ({ ...prev, seconds: Math.max(0, Math.min(59, value)) }));
                      }}
                      className="w-full px-3 md:px-4 py-2 md:py-3 border-2 border-gray-300 rounded-lg text-center text-base md:text-xl font-semibold focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400"
                    />
                  </div>
                </div>
              </div>
              
              {/* Focus Selection */}
              {(() => {
                const drill = completedDrills[editDrillState.drillIndex];
                const drillCategory = drill?.drill?.category;
                const applicableFocus = drillCategory ? getApplicableFocusForCategory(drillCategory) : [];
                
                if (applicableFocus.length === 0) return null;
                
                return (
                  <div className="mb-4 md:mb-6">
                    <FocusSelector
                      applicableFocus={applicableFocus}
                      selectedFocusIds={editDrillState.selectedFocusIds}
                      onFocusChange={(newFocusIds) => {
                        setEditDrillState(prev => ({ ...prev, selectedFocusIds: newFocusIds }));
                      }}
                    />
                  </div>
                );
              })()}
            </div>
            
            {/* Actions */}
            <div className="px-4 md:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row gap-2 md:gap-3">
              <button
                onClick={handleDeleteDrill}
                className="px-4 md:px-6 py-2 md:py-3 border-2 border-red-500 text-red-600 rounded-xl font-semibold text-sm md:text-base hover:bg-red-50 active:bg-red-100 transition-all hover:scale-105 active:scale-95"
              >
                {t('training.delete_drill')}
              </button>
              <div className="flex gap-2 md:gap-3 flex-1">
                <button
                  onClick={handleCloseEditDrill}
                  className="px-4 md:px-6 py-2 md:py-3 border-2 border-gray-300 rounded-xl font-semibold text-sm md:text-base hover:bg-gray-50 text-gray-700 transition-all hover:scale-105 active:scale-95 flex-1"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSaveDrillEdit}
                  className="px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-xl font-semibold text-sm md:text-base shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 flex-1"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}